import React, { useState } from 'react';
import { CheckCircleIcon, XCircleIcon, ArrowRightIcon, HelpCircleIcon } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
interface Question {
  id: number;
  type: 'multiple-choice' | 'fill-blank';
  question: {
    en: string;
    ar: string;
  };
  options?: {
    en: string;
    ar: string;
  }[];
  correctAnswer: string;
  explanation: {
    en: string;
    ar: string;
  };
}
interface ExercisePlayerProps {
  questions: Question[];
  onComplete: (score: number) => void;
}
export function ExercisePlayer({
  questions,
  onComplete
}: ExercisePlayerProps) {
  const {
    isArabic
  } = useLanguage();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answer, setAnswer] = useState('');
  const [showResult, setShowResult] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);
  const [score, setScore] = useState(0);
  const question = questions[currentQuestion];
  const isCorrect = answer === question.correctAnswer;
  const isLastQuestion = currentQuestion === questions.length - 1;
  const handleSubmit = () => {
    if (!showResult) {
      setShowResult(true);
      if (isCorrect) setScore(score + 1);
    } else if (isLastQuestion) {
      onComplete(score);
    } else {
      setCurrentQuestion(currentQuestion + 1);
      setAnswer('');
      setShowResult(false);
      setShowExplanation(false);
    }
  };
  return <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 max-w-3xl mx-auto" data-id="element-382">
      {/* Progress Bar */}
      <div className="mb-8" data-id="element-383">
        <div className="flex items-center justify-between mb-2" data-id="element-384">
          <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-385">
            {isArabic ? 'السؤال' : 'Question'} {currentQuestion + 1}/
            {questions.length}
          </span>
          <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-386">
            {isArabic ? 'النتيجة' : 'Score'}: {score}/{questions.length}
          </span>
        </div>
        <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-387">
          <div className="h-full bg-blue-600 dark:bg-blue-400 transition-all duration-300" style={{
          width: `${(currentQuestion + 1) / questions.length * 100}%`
        }} data-id="element-388" />
        </div>
      </div>
      {/* Question */}
      <div className="space-y-6 mb-8" data-id="element-389">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white" data-id="element-390">
          {isArabic ? question.question.ar : question.question.en}
        </h3>
        {question.type === 'multiple-choice' && question.options && <div className="space-y-3" data-id="element-391">
            {question.options.map((option, index) => <button key={index} onClick={() => !showResult && setAnswer(option.en)} className={`
                  w-full p-4 rounded-lg text-left transition-colors
                  ${answer === option.en ? showResult ? isCorrect ? 'bg-green-100 dark:bg-green-900/30 border-green-500' : 'bg-red-100 dark:bg-red-900/30 border-red-500' : 'bg-blue-100 dark:bg-blue-900/30 border-blue-500' : 'bg-gray-50 dark:bg-gray-900/50 hover:bg-gray-100 dark:hover:bg-gray-900 border-transparent'}
                  border-2
                `} disabled={showResult} data-id="element-392">
                <div className="flex items-start justify-between" data-id="element-393">
                  <div data-id="element-394">
                    <p className="font-medium text-gray-900 dark:text-white mb-1" data-id="element-395">
                      {isArabic ? option.ar : option.en}
                    </p>
                    <p className={isArabic ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400'} dir={isArabic ? 'rtl' : 'ltr'} data-id="element-396">
                      {isArabic ? option.en : option.ar}
                    </p>
                  </div>
                  {showResult && answer === option.en && (isCorrect ? <CheckCircleIcon className="w-6 h-6 text-green-500" data-id="element-397" /> : <XCircleIcon className="w-6 h-6 text-red-500" data-id="element-398" />)}
                </div>
              </button>)}
          </div>}
        {question.type === 'fill-blank' && <div className="space-y-4" data-id="element-399">
            <input type="text" value={answer} onChange={e => !showResult && setAnswer(e.target.value)} placeholder={isArabic ? 'اكتب إجابتك هنا' : 'Type your answer here'} className="w-full p-4 rounded-lg border-2 bg-gray-50 dark:bg-gray-900 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400" disabled={showResult} data-id="element-400" />
            {showResult && <div className={`flex items-center gap-2 ${isCorrect ? 'text-green-500' : 'text-red-500'}`} data-id="element-401">
                {isCorrect ? <CheckCircleIcon className="w-5 h-5" data-id="element-402" /> : <XCircleIcon className="w-5 h-5" data-id="element-403" />}
                <span data-id="element-404">
                  {isCorrect ? isArabic ? 'إجابة صحيحة!' : 'Correct answer!' : isArabic ? 'إجابة خاطئة' : 'Incorrect answer'}
                </span>
              </div>}
          </div>}
      </div>
      {/* Actions */}
      <div className="flex items-center justify-between" data-id="element-405">
        <button onClick={() => setShowExplanation(!showExplanation)} className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400" data-id="element-406">
          <HelpCircleIcon className="w-5 h-5" data-id="element-407" />
          <span data-id="element-408">{isArabic ? 'الشرح' : 'Explanation'}</span>
        </button>
        <button onClick={handleSubmit} className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" data-id="element-409">
          <span data-id="element-410">
            {showResult ? isLastQuestion ? isArabic ? 'إنهاء' : 'Finish' : isArabic ? 'السؤال التالي' : 'Next Question' : isArabic ? 'تحقق' : 'Check'}
          </span>
          <ArrowRightIcon className="w-4 h-4" data-id="element-411" />
        </button>
      </div>
      {/* Explanation */}
      {showExplanation && showResult && <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg" data-id="element-412">
          <h4 className="font-medium text-gray-900 dark:text-white mb-2" data-id="element-413">
            {isArabic ? 'الشرح' : 'Explanation'}
          </h4>
          <p className="text-gray-600 dark:text-gray-400 mb-2" data-id="element-414">
            {isArabic ? question.explanation.ar : question.explanation.en}
          </p>
          <p className="text-blue-600 dark:text-blue-400" dir={isArabic ? 'rtl' : 'ltr'} data-id="element-415">
            {isArabic ? question.explanation.en : question.explanation.ar}
          </p>
        </div>}
    </div>;
}